<template>
  <div class="et-root">

    <!-- HEADER -->
    <div class="et-header">
      <div class="et-header__left">
        <div class="et-icon-wrap">
          <v-icon size="15" color="#fff">mdi-chart-bar</v-icon>
        </div>
        <div class="et-header__meta">
          <span class="et-title">Error Trends</span>
          <span class="et-subtitle">Quality overview by period</span>
        </div>
      </div>
      <div class="et-filters">
        <div class="et-select-wrap">
          <v-icon size="13" color="#6366f1" class="et-cal-icon">mdi-calendar-month</v-icon>
          <select class="et-select" v-model="selectedQuarter" aria-label="Filter quarter">
            <option v-for="q in quarterOptions" :key="q.value" :value="q.value">{{ q.label }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- LOADER -->
    <div class="et-loader" v-if="loading">
      <div class="et-shimmer-bars">
        <div class="et-shimmer-bar" style="height: 60%"></div>
        <div class="et-shimmer-bar" style="height: 85%"></div>
        <div class="et-shimmer-bar" style="height: 45%"></div>
        <div class="et-shimmer-bar" style="height: 70%"></div>
        <div class="et-shimmer-bar" style="height: 90%"></div>
        <div class="et-shimmer-bar" style="height: 55%"></div>
        <div class="et-shimmer-bar" style="height: 75%"></div>
        <div class="et-shimmer-bar" style="height: 40%"></div>
        <div class="et-shimmer-bar" style="height: 65%"></div>
      </div>
    </div>

    <!-- BAR CHART -->
    <div class="et-chart-wrap" v-else-if="chartTrends.length">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <!-- EMPTY -->
    <div class="et-empty" v-else>
      <div class="et-empty__icon">
        <v-icon size="28" color="#e879a0">mdi-chart-bar-stacked</v-icon>
      </div>
      <span class="et-empty__title">No trend data</span>
      <span class="et-empty__sub">Data will appear once tasks are reviewed</span>
    </div>



  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import TimeSheetService from "@/api/service/TimeSheet/TimeSheetService";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default {
  props: ["userId"],
  data() {
    const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
    return {
      errorTrends: [],
      loading: false,
      chartInstance: null,
      selectedQuarter: currentQuarter,
      quarterOptions: [
        { value: 1, label: "Q1 Jan-Mar", short: "Q1" },
        { value: 2, label: "Q2 Apr-Jun", short: "Q2" },
        { value: 3, label: "Q3 Jul-Sep", short: "Q3" },
        { value: 4, label: "Q4 Oct-Dec", short: "Q4" },
      ],
    };
  },
  computed: {
    chartTrends() {
      const all = this.errorTrends;
      if (!all.length) return [];

      const quarterPeriodMap = {
        1: "JAN-MAR",
        2: "APR-JUN",
        3: "JUL-SEP",
        4: "OCT-DEC",
      };

      const find = (label) =>
        all.find(
          (d) =>
            d.period_type &&
            d.period_type.toUpperCase().includes(label.toUpperCase())
        );

      const week = find("THIS WEEK");
      const month = find("THIS MONTH");
      const year = find("THIS YEAR");

      // Get the selected quarter entry
      let quarterEntry = null;
      const selectedLabel = quarterPeriodMap[this.selectedQuarter];

      // Try to find the quarter directly from API data
      quarterEntry = find(selectedLabel);

      // If not found and it's Q1, derive from year minus other quarters
      if (!quarterEntry && this.selectedQuarter === 1 && year) {
        const otherQuarters = [2, 3, 4].map((q) => find(quarterPeriodMap[q]));
        const sumField = (field) =>
          otherQuarters.reduce((acc, d) => acc + (d ? d[field] || 0 : 0), 0);
        quarterEntry = {
          period_type: selectedLabel,
          total_issues: Math.max(0, (year.total_issues || 0) - sumField("total_issues")),
          pass: Math.max(0, (year.pass || 0) - sumField("pass")),
          sample: Math.max(0, (year.sample || 0) - sumField("sample")),
        };
      }

      // Build display rows: Week, Month, Quarter
      const rows = [];
      if (week) rows.push({ ...week, label: "This Week" });
      if (month) rows.push({ ...month, label: "This Month" });
      if (quarterEntry) rows.push({ ...quarterEntry, label: selectedLabel });

      return rows;
    },
  },
  watch: {
    userId(val) {
      if (val) this.getTrends();
    },
    selectedQuarter() {
      this.$nextTick(() => this.buildChart());
    },
    errorTrends() {
      this.$nextTick(() => this.buildChart());
    },
  },
  created() {
    this.getTrends();
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    async getTrends() {
      this.loading = true;
      try {
        const res = await TimeSheetService.getErrorTrends({
          userId: this.userId,
        });
        this.errorTrends = res || [];
      } finally {
        this.loading = false;
      }
    },

    buildChart() {
      this.destroyChart();
      const canvas = this.$refs.chartCanvas;
      if (!canvas || !this.chartTrends.length) return;

      const ctx = canvas.getContext("2d");
      const labels = this.chartTrends.map((d) => d.label);

      // Gradient fills for bars
      const mkGradient = (color1, color2) => {
        const gr = ctx.createLinearGradient(0, 0, 0, canvas.clientHeight || 160);
        gr.addColorStop(0, color1);
        gr.addColorStop(1, color2);
        return gr;
      };

      this.chartInstance = new Chart(canvas, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Total Tasks",
              data: this.chartTrends.map((d) => d.total_issues || 0),
              backgroundColor: mkGradient("#818cf8", "#6366f1"),
              borderColor: "transparent",
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false,
              barPercentage: 0.65,
              categoryPercentage: 0.55,
            },
            {
              label: "Errors",
              data: this.chartTrends.map((d) => d.sample || 0),
              backgroundColor: mkGradient("#fb7185", "#ef4444"),
              borderColor: "transparent",
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false,
              barPercentage: 0.65,
              categoryPercentage: 0.55,
            },
            {
              label: "Passed",
              data: this.chartTrends.map((d) => d.pass || 0),
              backgroundColor: mkGradient("#4ade80", "#22c55e"),
              borderColor: "transparent",
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false,
              barPercentage: 0.65,
              categoryPercentage: 0.55,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 8, bottom: 0, left: 0, right: 0 } },
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              labels: {
                boxWidth: 8,
                boxHeight: 8,
                borderRadius: 4,
                useBorderRadius: true,
                font: { size: 9.5, family: "'Inter', system-ui, sans-serif", weight: "500" },
                color: "#64748b",
                padding: 14,
              },
            },
            tooltip: {
              backgroundColor: "rgba(15,23,42,0.92)",
              titleFont: { size: 11, weight: "600", family: "'Inter', system-ui" },
              bodyFont: { size: 10.5, family: "'Inter', system-ui" },
              padding: { top: 8, bottom: 8, left: 12, right: 12 },
              cornerRadius: 10,
              displayColors: true,
              boxPadding: 4,
              callbacks: {
                label: (c) => ` ${c.dataset.label}: ${c.parsed.y}`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 9.5, family: "'Inter', system-ui", weight: "500" },
                color: "#64748b",
                maxRotation: 0,
              },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "rgba(241,245,249,0.8)", lineWidth: 1, drawBorder: false },
              ticks: {
                font: { size: 9, family: "'Inter', system-ui" },
                color: "#94a3b8",
                precision: 0,
                padding: 6,
              },
              border: { display: false },
            },
          },
        },
      });
    },

    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
  },
};
</script>

<style scoped>
.et-root {
  height: 100%;
  background: linear-gradient(168deg, #ffffff 0%, #fafbff 100%);
  border-radius: 18px;
  border: 1px solid rgba(99, 102, 241, 0.08);
  box-shadow:
    0 1px 3px rgba(99, 102, 241, 0.04),
    0 8px 24px rgba(99, 102, 241, 0.06);
  display: flex;
  flex-direction: column;
  padding: 16px 18px 14px;
  gap: 12px;
  overflow: hidden;
  position: relative;
}

/* subtle decorative accent */
.et-root::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  /* background: linear-gradient(90deg, #6366f1, #ef4444, #22c55e); */
  border-radius: 18px 18px 0 0;
  opacity: 0.7;
}

/* HEADER */
.et-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.et-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.et-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 8px rgba(99, 102, 241, 0.3);
}
.et-header__meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.et-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.3px;
}
.et-subtitle {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 400;
}

/* FILTERS */
.et-filters {
  display: flex;
  align-items: center;
}
.et-select-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 9px;
  background: rgba(99, 102, 241, 0.04);
  padding: 0 10px;
  height: 30px;
  transition: all 0.2s ease;
}
.et-select-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.08);
}
.et-cal-icon {
  flex-shrink: 0;
}
.et-select {
  height: 100%;
  border: none;
  background: transparent;
  color: #334155;
  font-size: 10.5px;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  padding: 0;
  min-width: 90px;
}

/* CHART */
.et-chart-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
}
.et-chart-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}



/* EMPTY */
.et-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.et-empty__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fef2f2, #fce7f3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.et-empty__title {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
}
.et-empty__sub {
  font-size: 10px;
  color: #94a3b8;
}

/* LOADER */
.et-loader {
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 20px 16px 24px;
}
.et-shimmer-bars {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
}
.et-shimmer-bar {
  flex: 1;
  max-width: 28px;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, #e2e8f0 0%, #f1f5f9 100%);
  animation: et-bar-pulse 1.2s ease-in-out infinite alternate;
}
.et-shimmer-bar:nth-child(1) { animation-delay: 0s; }
.et-shimmer-bar:nth-child(2) { animation-delay: 0.1s; }
.et-shimmer-bar:nth-child(3) { animation-delay: 0.2s; }
.et-shimmer-bar:nth-child(4) { animation-delay: 0.3s; }
.et-shimmer-bar:nth-child(5) { animation-delay: 0.4s; }
.et-shimmer-bar:nth-child(6) { animation-delay: 0.5s; }
.et-shimmer-bar:nth-child(7) { animation-delay: 0.6s; }
.et-shimmer-bar:nth-child(8) { animation-delay: 0.7s; }
.et-shimmer-bar:nth-child(9) { animation-delay: 0.8s; }
@keyframes et-bar-pulse {
  0% {
    opacity: 0.4;
    transform: scaleY(0.85);
  }
  100% {
    opacity: 1;
    transform: scaleY(1);
  }
}
</style>
